import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { Stripe, StripeElements, StripeElementChangeEvent } from '@stripe/stripe-js'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import { usePrevious } from '@/hooks/usePrevious'
import { styled } from '@mui/material/styles'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormHelperText from '@mui/material/FormHelperText'
import InputAdornment from '@mui/material/InputAdornment'
import LoadingButton from '@mui/lab/LoadingButton'
import MuiDivider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import CheckRoundedIcon from '@mui/icons-material/CheckRounded'

import TextField from '@/components/formik/fields/TextField'

import colors from '@/assets/scss/_themes-vars.module.scss'
import { Receipt } from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { PaymentType } from '@/core/models/consultation.model'
import TextInputController from '@/components/ui/inputs/TextInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'

export type PaymentProps = {
  consultationPrice: number
  paymentType?: PaymentType
  isConfiguringPayment?: boolean
  isDoctorStripePaymentActivated?: boolean
  isDoctor?: boolean
  isPaid?: boolean
  isPaying?: boolean
  isUpdatingPrice?: boolean
  receiptUrl?: string
  onChangePrice?: (price: number) => void
  onConnectOnboarding?: () => void
  onStartPayment?: ({ stripe, elements }: { stripe: Stripe; elements: StripeElements }) => void
  onManualPayment?: (price: number, paymentType: PaymentType) => Promise<void>
}

const priceUnitSymbolMap: Record<string, string> = {
  en: '$',
  fr: '€',
}

const Divider = styled(MuiDivider)({
  borderColor: colors.gray300,
})

const StripeElementContainer = styled('div')({
  padding: '9px 13px',
})

const manualPaymentSchema = yup.object({
  price: yup.number().required().positive(),
  paymentType: yup.string().required().oneOf(Object.values(PaymentType)),
})

type ManualPaymentFormValues = yup.InferType<typeof manualPaymentSchema>

export function Payment({ paymentType, ...props }: PaymentProps) {
  const {
    consultationPrice,
    isConfiguringPayment = false,
    isDoctorStripePaymentActivated = false,
    isDoctor = false,
    isPaid = false,
    isPaying = false,
    isUpdatingPrice = false,
    receiptUrl,
    onChangePrice,
    onConnectOnboarding,
    onStartPayment,
    onManualPayment
  } = props
  const { t, i18n } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const prevIsUpdatingPrice = usePrevious<boolean>(isUpdatingPrice)
  const [isManualMode, setIsManualMode] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [cardNumberComplete, setCardNumberComplete] = useState<boolean>(false)
  const [cardExpiryComplete, setCardExpiryComplete] = useState<boolean>(false)
  const [cardCvcComplete, setCardCvcComplete] = useState<boolean>(false)
  const [priceEditable, setPriceEditable] = useState<boolean>(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, StripeElementChangeEvent['error']>>({})

  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset
  } = useForm<ManualPaymentFormValues>({
    resolver: yupResolver(manualPaymentSchema),
    defaultValues: {
      price: 0,
      paymentType: PaymentType.Cash,
    }
  })

  useEffect(() => {
    if (!isUpdatingPrice && prevIsUpdatingPrice) {
      setPriceEditable(false)
    }
  }, [isUpdatingPrice, prevIsUpdatingPrice])

  const handleChange = (event: StripeElementChangeEvent) => {
    setValidationErrors(
      !event.error
        ? _.omit(validationErrors, event.elementType)
        : {
          ...validationErrors,
          [event.elementType]: event.error,
        }
    )
    switch (event.elementType) {
      case 'cardNumber':
        setCardNumberComplete(event.complete)
        break
      case 'cardExpiry':
        setCardExpiryComplete(event.complete)
        break
      case 'cardCvc':
        setCardCvcComplete(event.complete)
        break
      default:
        break
    }
  }

  const handleChangePrice = ({ price }: { price: number }) => {
    onChangePrice?.(price)
  }

  const handleAcceptPayment = (event: React.FormEvent<HTMLFormElement>) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    onStartPayment?.({ stripe, elements })
  }

  const togglePriceEditable = () => {
    setPriceEditable(!priceEditable)
  }

  const submitButtonDisabled = !stripe || !elements || !_.isEmpty(validationErrors) || !cardNumberComplete || !cardExpiryComplete || !cardCvcComplete

  const handleManualSubmit = async (values: ManualPaymentFormValues) => {
    if (!onManualPayment) return
    setIsSubmitting(true)
    try {
      await onManualPayment(values.price, values.paymentType as PaymentType)
      reset()
      setIsManualMode(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const paymentTypeOptions = Object.values(PaymentType).map(type => ({
    label: t(`page.liveConsultation.payment.type.${type.toLowerCase()}`),
    value: type
  }))

  const renderPaymentInfo = () => {
    if (!consultationPrice || !paymentType) return null;

    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: colors.gray100, borderRadius: 1 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2" color="textSecondary">
            {t('page.liveConsultation.payment.saved_info')}
          </Typography>
          <Stack direction="row" justifyContent="space-between">
            <Typography>{t('page.liveConsultation.payment.consultationPrice.label')}</Typography>
            <Typography>{consultationPrice} €</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography>{t('page.liveConsultation.payment.type.title')}</Typography>
            <Typography>{t(`page.liveConsultation.payment.type.${paymentType.toLowerCase()}`)}</Typography>
          </Stack>
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Paper
        sx={{
          borderRadius: '8px',
          boxShadow: '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
          padding: '24px',
          minWidth: '400px',
        }}
      >
        <Stack spacing={2}>
          <Stack alignItems="center" justifyContent="center" spacing={2}>
            <Avatar sx={{ backgroundColor: colors.green100 }}>
              <CheckRoundedIcon htmlColor={colors.green600} />
            </Avatar>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, lineHeight: '24px', color: colors.gray900 }}>
              {isPaid ? t('page.liveConsultation.payment.title', { context: 'paid' }) : t('page.liveConsultation.payment.title')}
            </Typography>
            {isPaid && receiptUrl && (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <Receipt />
                <Button href={receiptUrl} target="_blank">
                  {t('page.liveConsultation.payment.receiptButton.label')}
                </Button>
              </Stack>
            )}
          </Stack>
          <Stack spacing={1} sx={{ pl: '24px', pr: '24px' }}>
            {renderPaymentInfo()}

            {isDoctorStripePaymentActivated ? (
              <>
                {!priceEditable && (
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{t('page.liveConsultation.payment.consultationPrice.label')}</Typography>
                    {/**
                     * TODO: price unit should be retrieved from backend
                     */}
                    <Typography sx={{ fontSize: '14px' }}>{consultationPrice} €</Typography>
                  </Stack>
                )}
                {!isDoctor && !isPaid && (
                  <form onSubmit={handleAcceptPayment}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography component="div" sx={{ fontSize: '14px', fontWeight: 500, lineHeight: '20px', mb: '4px' }}>
                          {t('page.liveConsultation.payment.creditCard.label')}
                        </Typography>
                        <Stack sx={{ border: `1px solid ${colors.gray300}`, borderRadius: '6px', boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)' }}>
                          <StripeElementContainer>
                            <CardNumberElement onChange={handleChange} options={{ placeholder: t('field.cardNumber.placeholder') }} />
                          </StripeElementContainer>
                          <Divider />
                          <Stack direction="row">
                            <StripeElementContainer sx={{ flex: 1 }}>
                              <CardExpiryElement onChange={handleChange} />
                            </StripeElementContainer>
                            <Divider orientation="vertical" flexItem />
                            <StripeElementContainer sx={{ flex: 1 }}>
                              <CardCvcElement onChange={handleChange} />
                            </StripeElementContainer>
                          </Stack>
                        </Stack>
                        <Stack>
                          {_.map(validationErrors, (error, elementType: string) => (
                            <FormHelperText key={elementType} error sx={{ position: 'relative' }}>
                              {error?.message}
                            </FormHelperText>
                          ))}
                        </Stack>
                      </Box>
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} paddingTop="24px">
                        <LoadingButton disabled={submitButtonDisabled} loading={isPaying} type="submit" variant="contained">
                          {t('page.liveConsultation.payment.acceptPaymentButton.label')}
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  </form>
                )}
                {isDoctor && (
                  <>
                    {!!priceEditable ? (
                      <Formik
                        initialValues={{ price: consultationPrice }}
                        onSubmit={handleChangePrice}
                        validationSchema={yup.object().shape({
                          price: yup.number().moreThan(0).required(),
                        })}
                      >
                        {() => (
                          <Form>
                            <Stack spacing={4}>
                              <TextField
                                fullWidth
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">{priceUnitSymbolMap[i18n.language]}</InputAdornment>,
                                }}
                                label={t('field.price.label')}
                                name="price"
                                type="number"
                              />
                              <Stack direction="row" spacing={1}>
                                <Button fullWidth onClick={togglePriceEditable} variant="neutral">
                                  {t('common:button.cancel.label')}
                                </Button>
                                <LoadingButton loading={isUpdatingPrice} fullWidth type="submit" variant="contained">
                                  {t('common:button.submit.label')}
                                </LoadingButton>
                              </Stack>
                            </Stack>
                          </Form>
                        )}
                      </Formik>
                    ) : (
                      <Stack direction="row" alignItems="center" justifyContent="center" paddingTop="24px">
                        <Button onClick={togglePriceEditable} variant="contained">
                          {t('common:button.modify.label')}
                        </Button>
                      </Stack>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <Typography>{t('page.liveConsultation.payment.doctorStripePaymentNotActivated.message')}</Typography>
                {isDoctor && (
                  <LoadingButton loading={isConfiguringPayment} onClick={onConnectOnboarding} variant="contained">
                    {t('page.liveConsultation.payment.configurePaymentButton.label')}
                  </LoadingButton>
                )}
              </>
            )}

            {!isManualMode ? (
              <Stack direction="row" alignItems="center" justifyContent="center" paddingTop="24px">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setIsManualMode(true)}
                >
                  {t('page.liveConsultation.payment.manual_mode')}
                </Button>
              </Stack>
            ) : (
              <form onSubmit={handleSubmit(handleManualSubmit)}>
                <Stack spacing={2}>
                  <TextInputController
                    control={control}
                    name="price"
                    label={t('page.liveConsultation.payment.consultationPrice.label')}
                    type="number"
                    fullWidth
                  />

                  <SelectInputController
                    control={control}
                    name="paymentType"
                    label={t('page.liveConsultation.payment.type.title')}
                    options={paymentTypeOptions}
                    fullWidth
                  />

                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        reset()
                        setIsManualMode(false)
                      }}
                    >
                      {t('common:button.cancel.label')}
                    </Button>

                    <LoadingButton
                      variant="contained"
                      type="submit"
                      loading={isSubmitting}
                      disabled={!isValid}
                    >
                      {t('common:button.submit.label')}
                    </LoadingButton>
                  </Stack>
                </Stack>
              </form>
            )} 
          </Stack>
        </Stack>
      </Paper>
    </Box>
  )
}

export default Payment
